import React, { useEffect, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Popover } from 'react-tiny-popover';

import { FormAPI } from 'lecom-api';
import { formatDatetime, formatToNumber } from 'utils/formatting_utils';
import FlashMessages from '../../../../flash_message/components/flash_messages.jsx';
import { InputGroup } from '../../../../form/components/form/input_group';
import FormFieldTypes from '../../../../form/constants/form_field_types';
import { FormEvents } from '../../../../form/events/form_event_handler';
import { GridFormActions, GridFormStore } from '../../../../grid_form/reflux/grid_form_store';
import ButtonGroup from '../../../../realize_overrides/components/button/button_group.jsx';
import Modal from '../../../../realize_overrides/components/modal/modal.jsx';
import { ModalActions } from '../../../../realize_overrides/stores/modal_store';
import { generateUUID } from '../../../../utils/uuid';
import DocumentModalIds from '../../../constants/document_modal_ids';
import DocumentSubmitDataContainers from '../../../constants/document_submit_data_containers';
import DocumentValidationsErrors from '../../../constants/document_validations_errors';
import { DocumentActions } from '../../../reflux/document_store';

import "./styles.scss";

const Realize = {
  i18n: window.Realize.i18n,
  Modal: Modal,
  ModalHeader: window.ModalHeader,
  ModalContent: window.ModalContent,
  ModalFooter: window.ModalFooter,
};

const acceptStyle = {
  color: '#1976D2',
	borderColor: '#1976D2'
};

const rejectStyle = {
  color: '#ff1744',
	borderColor: '#ff1744'
};

const ImportMultipleGridModal = ({ fields, isMobile }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filesList, setFilesList] = useState([]);
  const [popoversOpen, setPopoversOpen] = useState([]);
  const [startQueue, setStartQueue] = useState(false);
  const [countFileImported, setCountFileImported] = useState(0);
  const [initialFieldsValues, setInitialFieldsValues] = useState([]);

  const {
    acceptedFiles,
    getRootProps,
    getInputProps,
    isDragAccept,
		isDragReject
  } = useDropzone({ disabled: startQueue });

	const style = useMemo(() => ({
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isDragAccept, isDragReject]
  );

  const importFiles = () => {
		const modalContentElem = document.querySelector("#importMultipleGridModal .modal-content-container");

    if (modalContentElem) {
      modalContentElem.scrollTo(0, 0);
    }

    setFilesList(
      filesList.map((f) => {
        const formApi = FormAPI.form(documentField().inputProps.formId);
        const fieldsGridApi = formApi
          .fields(window.importMultipleGridId)
          .fields()
          .filter((fg) => fg.type() !== FormFieldTypes.DOCUMENT);
        let fieldsGridValues = f.fieldsGridValues || [];

        if (f.fieldsGridVisible) {
          fieldsGridValues = fieldsGridApi.map((v) => ({
            id: v.id,
            value:
              v.value() !== null && v.value() !== undefined && v.value() !== ''
                ? v.value()
                : '',
          }));
        }

        return {
          ...f,
          metadataVisible: false,
          fieldsGridVisible: false,
          fieldsGridValues,
        };
      })
    );

    setStartQueue(true);
  };

  const dismissModal = () => {
    ModalActions.close({ modalId: 'importMultipleGridModal' });
    setIsOpen(false);
    window.importMultipleGridOpen = false;

    setTimeout(() => {
      DocumentActions.setValueInDocument(documentField().inputId, '');
    }, 1000);
  };

  const buildDismissButtonProps = () => {
    return {
      name: Realize.i18n.t('actions.close'),
      element: 'a',
      style: 'cancel',
      role: 'button',
      icon: 'close',
      onClick: dismissModal,
      className: `button-close-modal-document ${startQueue ? 'disabled' : ''}`,
      disabled: startQueue,
    };
  };

  const buildImportButtonProps = () => {
    return {
      name: Realize.i18n.t('exportMultipleModal.actions.load'),
      element: 'a',
      style: 'primary',
      role: 'button',
      icon: 'file_upload',
      onClick: importFiles,
      className: `button-import-document ${startQueue ? 'disabled' : ''}`,
      disabled: startQueue,
    };
  };

  const buildButtonGroupProps = () => {
    return [buildDismissButtonProps(), buildImportButtonProps()];
  };

  const renderModalHeader = () => {
		return (<span className="h5-custom">{Realize.i18n.t("importMultipleModal.modalTitleGrid")}</span>);
	};

  const renderModalButtons = () => {
    return <ButtonGroup buttons={buildButtonGroupProps()} />;
  };

  const removeFile = (id) => {
    setFilesList(filesList.filter((f) => f.id !== id));
  };

  const hasMetadata = () => {
    return (
      !!Object.keys(documentField()).length &&
      !!Object.keys(documentField().template.fields).length
    );
  };

  const hasFields = () => {
    return !!getFieldsGridFormStore().filter(
      (f) => f.type !== FormFieldTypes.DOCUMENT
    ).length;
  };

  const getFieldsGridFormStore = () => {
    const gridFormStore = GridFormStore.store[window.importMultipleGridId];
    return gridFormStore.fields
      ? gridFormStore.fields.filter((f) => f.id !== 'id')
      : [];
  };

  const documentField = () => {
    const documentFieldGrid = getFieldsGridFormStore().find(
      (f) => f.type === FormFieldTypes.DOCUMENT
    );

    return Lecom.stores.DocumentStore.store[documentFieldGrid.id];
  };

  const getAssociatedFields = () => {
    const currentDcumentField = documentField()
    const gridField = fields[window.importMultipleGridId]

    const documentFieldData = gridField.fields.find((field) => {
      return field.id === currentDcumentField.inputId
    })

    const formFieldMappings = documentFieldData.formFieldMappings
    const isSyncMetadataEnabled = documentFieldData.syncMetadata
    const hasNoAssociatedsMetadata = formFieldMappings === undefined

    if (!isSyncMetadataEnabled || hasNoAssociatedsMetadata) {
      return []
    }

    const associatedFieldNames = formFieldMappings.map((field) => {
      return field.documentField
    })

    return associatedFieldNames
  }

  const modalReady = () => {
    setFilesList(filesList.filter((f) => !f.successImport));
    setIsOpen(true);

    const formApi = FormAPI.form(documentField().inputProps.formId);
    const fieldsGridApi = formApi
      .fields(window.importMultipleGridId)
      .fields()
      .filter((fg) => fg.type() !== FormFieldTypes.DOCUMENT);

    const fieldsGridValues = fieldsGridApi.map((v) => {
      return {
        id: v.id,
        value:
          v.value() !== null && v.value() !== undefined && v.value() !== ''
            ? v.value()
            : '',
      };
    });

    setInitialFieldsValues(fieldsGridValues);
  };

  const toggleVisibleMetadata = (id) => {
    setFilesList(
      filesList.map((f) => {
        if (f.gridIdRef !== window.importMultipleGridId) {
          return { ...f, fieldsGridVisible: false, metadataVisible: false };
        }

        let fieldsGridValues = f.fieldsGridValues || initialFieldsValues;

        if (f.metadataVisible) {
          return {
            ...f,
            metadataVisible: false,
            fieldsGridVisible: false,
            fieldsGridValues,
          };
        }

        if (f.fieldsGridVisible) {
          const formApi = FormAPI.form(documentField().inputProps.formId);
          const fieldsGridApi = formApi
            .fields(window.importMultipleGridId)
            .fields()
            .filter((fg) => fg.type() !== FormFieldTypes.DOCUMENT);
          fieldsGridValues = fieldsGridApi.map((v) => ({
            id: v.id,
            value:
              v.value() !== null && v.value() !== undefined && v.value() !== ''
                ? v.value()
                : '',
          }));
        }

        if (f.id === id) {
          return {
            ...f,
            metadataVisible: true,
            fieldsGridVisible: false,
            fieldsGridValues,
          };
        }

        return {
          ...f,
          metadataVisible: false,
          fieldsGridVisible: false,
          fieldsGridValues,
        };
      })
    );
  };

  const updateRadioFieldValue = (field, value, event) => {
    const containerFieldsGrid = $(event.target).parent().parent()
    const contentFieldsGrid = containerFieldsGrid[0]

    const inputRadio = `div#input__${field.name()} input`

    setTimeout(() => {
      const valueIsString = typeof value === 'string'
      const fieldValue = valueIsString ? value : value[0]

      $(contentFieldsGrid)
        .find(inputRadio)
        .each(function () {
          if ($(this)[0].defaultValue === fieldValue) {
            $($(this)[0]).prop('checked', true);
            return;
          }

          $($(this)[0]).prop('checked', false)
        })
    }, 0)
  }

  const updateFieldsGridValues = (fieldsGridValues, event) => {
    const formApi = FormAPI.form(documentField().inputProps.formId)
    const fieldsGridApi = formApi
      .fields(window.importMultipleGridId)
      .fields()
      .filter((fg) => fg.type() !== FormFieldTypes.DOCUMENT)

    fieldsGridApi.forEach((field) => {
      const findValue = fieldsGridValues.find((v) => v.id === field.id)
      const fieldHasInitialValues =
        findValue &&
        findValue.value !== null &&
        findValue.value !== undefined &&
        findValue.value !== '';

      const value = fieldHasInitialValues ? findValue.value : ''

      field.value(value).apply()

      if (field.type() === 'RADIO' && value.length > 0) {
        updateRadioFieldValue(field, value, event)
      }
    })
  }

  const toggleVisiblefieldsGrid = (id, event) => {
    const fileListUpdated = filesList.map((f) => {
      if (f.gridIdRef !== window.importMultipleGridId) {
        return { ...f, fieldsGridVisible: false, metadataVisible: false }
      }

      if (f.fieldsGridVisible) {
        const formApi = FormAPI.form(documentField().inputProps.formId)
        const fieldsGridApi = formApi
          .fields(window.importMultipleGridId)
          .fields()
          .filter((fg) => fg.type() !== FormFieldTypes.DOCUMENT);

        const fieldsGridValues = fieldsGridApi.map((v) => {
          return {
            id: v.id,
            value:
              v.value() !== null &&
              v.value() !== undefined &&
              v.value() !== ''
                ? v.value()
                : '',
          };
        });

        return {
          ...f,
          fieldsGridVisible: false,
          metadataVisible: false,
          fieldsGridValues,
        };
      }

      if (f.id === id) {
        const hasFieldsGridValues =
          f.fieldsGridValues !== undefined && f.fieldsGridValues.length > 0;

        const fieldsGridValues = hasFieldsGridValues
          ? f.fieldsGridValues
          : initialFieldsValues;

        updateFieldsGridValues(fieldsGridValues, event);

        return {
          ...f,
          fieldsGridVisible: true,
          metadataVisible: false,
          fieldsGridValues,
        };
      }

      return { ...f, fieldsGridVisible: false, metadataVisible: false };
    })

    setFilesList(fileListUpdated);
  };

  const changePopoversOpen = (fileId, isOpen) => {
    if (popoversOpen.find((p) => p.fileId === fileId)) {
      setPopoversOpen(
        popoversOpen.map((p) => {
          if (p.fileId === fileId) {
            return { ...p, isOpen };
          }

          return p;
        })
      );
    } else {
      setPopoversOpen([...popoversOpen, { fileId, isOpen }]);
    }
  };

  const getPopoverIsOpen = (fileId) => {
    const popover = popoversOpen.find((p) => p.fileId === fileId);
    return popover ? popover.isOpen : false;
  };

  const contentPopoverError = (file) => {
    const { error } = file;
    let errorFormatted = error.message;

    if (errorFormatted === DocumentValidationsErrors.DUPLICATED_DOCUMENTS) {
      errorFormatted = Realize.i18n.t(
        'importMultipleModal.filesListErrors.duplicatedDocuments'
      );
    } else if (
      errorFormatted === DocumentValidationsErrors.DIVERGENT_VERSION_IN_CHECKIN
    ) {
      errorFormatted = Realize.i18n.t(
        'importMultipleModal.filesListErrors.divergentVersionInCheckin'
      );
    } else if (errorFormatted === DocumentValidationsErrors.CHECKOUT_UNDONE) {
      errorFormatted = Realize.i18n.t(
        'importMultipleModal.filesListErrors.checkoutUndone'
      );
    } else if (error.code === 'file_type_restriction_exception') {
      errorFormatted = Realize.i18n
        .t(
          `importMultipleModal.filesListErrors.file_type_restriction_exception`
        )
        .replace('@', file.data.name.split('.').pop());
    } else if (error.code === 'file_size_restriction_exception') {
      errorFormatted = Realize.i18n
        .t(
          `importMultipleModal.filesListErrors.file_size_restriction_exception`
        )
        .replace('@', documentField().template.maxFileSizeAllowed);
    } else if (error.code) {
      errorFormatted = Realize.i18n.t(
        `importMultipleModal.filesListErrors.${error.code}`
      );
    }

    return (
      <div className="content-popover-error">
        <ul>
          <li>{errorFormatted}</li>
        </ul>
      </div>
    );
  };

  const insertDataRowUpload = (file, addCount) => {
    const { fieldsGridValues, id } = file;
    const formApi = FormAPI.form(documentField().inputProps.formId);
    const gridApi = formApi.fields(window.importMultipleGridId);
		const fieldsGrid = fieldsGridValues && Array.isArray(fieldsGridValues) && !!fieldsGridValues.length ? fieldsGridValues : null;
		const initialFieldsGrid = initialFieldsValues && Array.isArray(initialFieldsValues) && !!initialFieldsValues.length ? initialFieldsValues : null;

    (fieldsGrid || initialFieldsGrid || []).forEach((field) => {
      if (gridApi.fields(field.id)) {
        gridApi.fields(field.id).value(field.value);
      }
    });

    formApi.apply().then(() => {
      GridFormActions.submitDataRow(window.importMultipleGridId);

      setTimeout(() => {
        setFilesList(
          filesList.map((f) => {
            if (f.id === id) {
              return {
                ...f,
                error: {},
                successImport: true,
              };
            }

            return f;
          })
        );

        if (addCount !== null && addCount !== undefined) {
          setCountFileImported(addCount + 1);
        }
      }, 1000);
    });
  };

  const importFileByQueue = (index) => {
    const file = filesList[index];
		const metadataFields = documentField().template && documentField().template.fields ? documentField().template.fields : {};
    const errorsMetadata = {};

    Object.keys(metadataFields).forEach((key) => {
      const field = metadataFields[key];
      const value = file.metadataValues[`${key}_IMPORT_templateField`];

      if (
        field.validations &&
        Array.isArray(field.validations) &&
        field.validations.find((v) => v.required)
      ) {
        if (value !== null && value !== undefined && value !== '') {
          delete errorsMetadata[`${key}_IMPORT_templateField`];
        } else {
          errorsMetadata[`${key}_IMPORT_templateField`] = Realize.i18n.t(
            'form.validations.required'
          );
        }
      } else {
        delete errorsMetadata[`${key}_IMPORT_templateField`];
      }
    });

    const formApi = FormAPI.form(documentField().inputProps.formId);
    const fieldsGridApi = formApi
      .fields(window.importMultipleGridId)
      .fields()
      .filter((fg) => fg.type() !== FormFieldTypes.DOCUMENT);
    const fieldsGridRequires = fieldsGridApi.filter((fg) => fg.required());
    const errorsGrid = fieldsGridRequires.reduce((acc, cur) => {
      const fieldsValues = file.fieldsGridValues;
      const findField = fieldsValues.find((v) => v.id === cur.id) || {};
      const value = findField.value;

      if (
        value === undefined ||
        value === null ||
        value === '' ||
        (Array.isArray(value) && !value.length)
      ) {
        return {
          ...acc,
          [cur.id]: Realize.i18n.t('form.validations.required'),
        };
      }

      return acc;
    }, {});

    const isFileFromAnotherGrid =
      file.gridIdRef !== window.importMultipleGridId;

    if (file.successImport || isFileFromAnotherGrid) {
      setCountFileImported(index + 1);
    } else if (
      Object.keys(errorsMetadata).length ||
      Object.keys(errorsGrid).length
    ) {
      let emptyFields = `${Realize.i18n.t(
        'importMultipleModal.filesListErrors.emptyFieldsMetadata'
      )} ${Realize.i18n.t(
        'importMultipleModal.filesListErrors.emptyFieldsAnd'
      )} ${Realize.i18n.t(
        'importMultipleModal.filesListErrors.emptyFieldsGrid'
      )}`;

      if (
        Object.keys(errorsMetadata).length &&
        !Object.keys(errorsGrid).length
      ) {
        emptyFields = `${Realize.i18n.t(
          'importMultipleModal.filesListErrors.emptyFieldsMetadata'
        )}`;
      } else if (
        !Object.keys(errorsMetadata).length &&
        Object.keys(errorsGrid).length
      ) {
        emptyFields = `${Realize.i18n.t(
          'importMultipleModal.filesListErrors.emptyFieldsGrid'
        )}`;
      }

      setFilesList(
        filesList.map((f) => {
          if (f.id === file.id) {
            return {
              ...f,
              error: {
                message: `${Realize.i18n.t(
                  'importMultipleModal.filesListErrors.emptyFieldsBegin'
                )} ${emptyFields} ${Realize.i18n.t(
                  'importMultipleModal.filesListErrors.emptyFieldsEnd'
                )}`,
              },
              successImport: false,
              errorsMetadata,
              errorsGrid,
            };
          }

          return f;
        })
      );

      setCountFileImported(index + 1);
    } else {
      const gridApi = formApi.fields(window.importMultipleGridId);
      const fieldDocumentApi = gridApi.fields(documentField().inputId);
			const metadataFields = documentField().template && documentField().template.fields ? documentField().template.fields : {};
      const metadataValues = file.metadataValues;

			DocumentActions.setSubmitDataField(documentField().inputId, `${DocumentSubmitDataContainers.IMPORT}.file`, file.data);
			DocumentActions.setDocumentFileSelected(documentField().inputId, file.data);

      for (const key in metadataFields) {
        const metadata = metadataFields[key];
        const findValue = metadataValues[`${key}_IMPORT_templateField`];
				let value = findValue !== null && findValue !== undefined && findValue !== "" ? findValue : "";

        if (metadata.type === FormFieldTypes.CURRENCY)
          value = formatToNumber(value);
        if (metadata.type === FormFieldTypes.DATE)
          value = formatDatetime(value);
				if (metadata.type === FormFieldTypes.NUMBER && typeof value === "string")
          value = value.replace(/[^-+\d]/g, '');

				DocumentActions.setSubmitMetadataField(documentField().inputId, "IMPORT", metadata.id, value);
      }

      DocumentActions.importDocument(documentField().inputId, true);

      const subscribeSuccess = fieldDocumentApi.subscribe(
        FormEvents.DOCUMENT_IMPORT_SUCCESS,
        () => {
          subscribeSuccess.unsubscribe();

          if (subscribeError) {
            subscribeError.unsubscribe();
          }

          insertDataRowUpload(file, index);
        }
      );

      const subscribeError = fieldDocumentApi.subscribe(
        FormEvents.DOCUMENT_IMPORT_ERROR,
        (formId, inputId, error, response) => {
          subscribeError.unsubscribe();

          if (subscribeSuccess) {
            subscribeSuccess.unsubscribe();
          }

          let errorCode = '';
          let errorMessage = '';

          if (
            error === DocumentValidationsErrors.DUPLICATED_DOCUMENTS ||
            error === DocumentValidationsErrors.DIVERGENT_VERSION_IN_CHECKIN ||
            error === DocumentValidationsErrors.CHECKOUT_UNDONE
          ) {
            errorMessage = error;
          } else {
            const objError =
              error &&
              error.entity &&
              error.entity.errors &&
              Array.isArray(error.entity.errors) &&
              error.entity.errors.length
                ? error.entity.errors[0]
                : {};

					if (objError.code === DocumentValidationsErrors.NULL_POINTER_EXCEPTION) {
						errorMessage = Realize.i18n.t('form.errors.null_pointer_exception');
					}
					else if (objError.message) {
              errorMessage = objError.message;
              errorCode = objError.code;
            }
          }

          setFilesList(
            filesList.map((f) => {
              if (f.id === file.id) {
                return {
                  ...f,
                  error: { code: errorCode, message: errorMessage, response },
                  successImport: false,
                };
              }

              return f;
            })
          );

          setCountFileImported(index + 1);
        }
      );
    }
  };

  const clickError = (file) => {
    const { error } = file;

    if (error.message === DocumentValidationsErrors.DUPLICATED_DOCUMENTS) {
      const formApi = FormAPI.form(documentField().inputProps.formId);
      const gridApi = formApi.fields(window.importMultipleGridId);
      const fieldDocumentApi = gridApi.fields(documentField().inputId);

			DocumentActions.errorDuplicatedDocuments(documentField().inputId, DocumentModalIds.IMPORT_DOCUMENT, error.response);

			const subscribeSuccess = fieldDocumentApi.subscribe(FormEvents.DUPLICATED_DOCUMENTS_SUCCESS, () => {
          subscribeSuccess.unsubscribe();
          insertDataRowUpload(file);

          if (countFileSuccess() + 1 === filesList.length) {
            dismissModal();
          }
        }
      );
    }
  };

  const getFieldsMetadata = (id) => {
		const metadataFields = documentField().template && documentField().template.fields ? documentField().template.fields : {};
    let metafields = {};

    Object.keys(metadataFields).forEach((key) => {
      metafields[key] = {
        ...metadataFields[key],
        id: `${key}_IMPORT_templateField`,
        required:
          metadataFields[key].validations &&
          Array.isArray(metadataFields[key].validations) &&
          !!metadataFields[key].validations.find((v) => v.required),
        onChange: (event, fieldValue) => {
          let dataFieldValue = Array.isArray(fieldValue)
            ? fieldValue[0]
            : fieldValue;

          setFilesList(
            filesList.map((f) => {
              if (f.id === id) {
                return {
                  ...f,
                  metadataValues: {
                    ...f.metadataValues,
                    [`${key}_IMPORT_templateField`]: dataFieldValue,
                  },
                };
              }

              return f;
            })
          );

          return false;
        },
      };
    });

    return metafields;
  };

  const getFieldsGrid = () => {
    const gridFields = getFieldsGridFormStore();
    let fields = {};

    gridFields.forEach((key) => {
      if (key.type !== 'DOCUMENT') {
        fields[key.id] = key;
      }
    });

    return fields;
  };

  const countFileSuccess = () => {
    return filesList.reduce((acc, cur) => {
      if (cur.successImport) {
        return acc + 1;
      }

      return acc;
    }, 0);
  };

  const keyUpElement = (e) => {
    if (e.which === 13 || e.keyCode === 13) {
      e.currentTarget.click();
    }
  };

  useEffect(() => {
    if (startQueue && countFileImported + 1 <= filesList.length) {
      importFileByQueue(countFileImported);
    } else {
      if (startQueue) {
        setStartQueue(false);
        setCountFileImported(0);

        const countFilesFromCurrentGrid = filesList.filter((file) => {
          return file.gridIdRef === window.importMultipleGridId;
        }).length;

        if (countFileSuccess() === countFilesFromCurrentGrid) {
          dismissModal();
        }
      }
    }
  }, [startQueue, countFileImported]);

  useEffect(() => {
    if (acceptedFiles.length) {
      const mappingFields = documentField().inputProps.formFieldMappings;
      const formApi = FormAPI.form(documentField().inputProps.formId);
      const metadataValues = (mappingFields || []).reduce((acc, cur) => {
        const fieldApi = cur.formField.gridFormParent
          ? formApi
              .fields(cur.formField.gridFormParent)
              .fields(cur.formField.id)
          : formApi.fields(cur.formField.id);

        return {
          ...acc,
          [`${cur.documentField}_IMPORT_templateField`]: fieldApi.value(),
        };
      }, {});

      const gridId = window.importMultipleGridId;

      const filesMapped = acceptedFiles.map((f) => {
        return {
          id: generateUUID(),
          data: f,
          error: {},
          metadataValues,
          gridIdRef: gridId,
        };
      });

      setFilesList([...filesList, ...filesMapped]);
    }
  }, [acceptedFiles]);

  return (
    <div
      className={`container-import-multiple-grid-modal ${isOpen ? 'open' : ''}`}
    >
      <Realize.Modal
        id="importMultipleGridModal"
        key="importMultipleGridModal"
        dismissible={false}
        ready={modalReady}
      >
        <Realize.ModalHeader>{renderModalHeader()}</Realize.ModalHeader>
        <Realize.ModalContent>
          <div className="modal-container-import-multiple">
            <FlashMessages id="flash-modal-import-multiple" />

            <div
              {...getRootProps({
                style,
                className: `base-style drop-zone ${
                  startQueue ? 'disabled' : ''
                }`,
              })}
            >
              <input data-testid='file-input-grid' {...getInputProps()} />
              <p>{Realize.i18n.t('importMultipleModal.dropZone')}</p>
            </div>

            {!!filesList.length && (
              <ul className="files">
                {filesList.map((file) => {
                  if (file.gridIdRef !== window.importMultipleGridId) {
                    return <></>;
                  }

                  return (
                    <li
                      key={file.id}
                      className={`${
                        hasMetadata() ? 'has-fields-metadata' : ''
                      } ${hasFields() ? 'has-fields-grid' : ''}`}
                    >
                      {file.successImport && (
                        <div className="block-success"></div>
                      )}
                      <div className="container-file">
                        <span
                          className="document-name"
                          title={documentField().inputProps.label}
                        >
                          {documentField().inputProps.label}
                        </span>

                        <i className="material-icons attach-file">
                          attach_file
                        </i>

                        <span className="file-name" title={file.data.name}>
                          {file.data.name}
                        </span>

                        <div className={`container-actions file-id-${file.id}`}>
                          {startQueue && !file.successImport ? (
                            <span class="loader-import"></span>
                          ) : file.successImport ? (
                            <i
                              className="material-icons success-import"
                              title={Realize.i18n.t(
                                'importMultipleModal.successImport'
                              )}
                            >
                              check
                            </i>
                          ) : (
                            <>
                              {file.error && file.error.message && (
                                <Popover
                                  isOpen={getPopoverIsOpen(file.id)}
                                  positions={['left', 'top', 'bottom', 'right']}
                                  content={contentPopoverError(file)}
                                  parentElement={document.querySelector(
                                    `#importMultipleGridModal .container-actions.file-id-${file.id}`
                                  )}
                                >
                                  <i
                                    className="material-icons alert-error"
                                    onMouseOver={() =>
                                      changePopoversOpen(file.id, true)
                                    }
                                    onMouseOut={() =>
                                      changePopoversOpen(file.id, false)
                                    }
                                    onClick={() => clickError(file)}
                                    tabIndex="0"
                                    onKeyUp={keyUpElement}
                                  >
                                    error_outline
                                  </i>
                                </Popover>
                              )}
                              <i
                                className="material-icons remove-file"
                                onClick={() => removeFile(file.id)}
                                title={Realize.i18n.t(
                                  'importMultipleModal.removeFile'
                                )}
                                tabIndex="0"
                                onKeyUp={keyUpElement}
                              >
                                remove_circle_outline
                              </i>
                            </>
                          )}
                        </div>

                        {hasMetadata() && (
                          <div className="container-fields-metadata">
                            <div
                              className={`open-fields-metadata ${
                                file.metadataVisible ? 'is-open' : ''
                              }`}
                              onClick={() => toggleVisibleMetadata(file.id)}
                              tabIndex="0"
                              onKeyUp={keyUpElement}
                            >
                              {file.metadataVisible ? (
                                <i className="material-icons">expand_less</i>
                              ) : (
                                <i className="material-icons">expand_more</i>
                              )}

                              <span className="title">
                                {Realize.i18n.t(
                                  'importMultipleModal.metadataFields'
                                )}
                              </span>
                            </div>

                            <div
                              className={`content-fields-metadata ${
                                file.metadataVisible ? 'is-open' : ''
                              }`}
                            >
                              <InputGroup
                                inputs={getFieldsMetadata(file.id)}
                                data={file.metadataValues}
                                errors={file.errorsMetadata}
                                metadataFieldsToDisable={getAssociatedFields()}
                              />
                            </div>
                          </div>
                        )}

                        {hasFields() && (
                          <div className="container-fields-grid">
                            <div
                              className={`open-fields-grid ${
                                file.fieldsGridVisible ? 'is-open' : ''
                              }`}
                              onClick={(event) =>
                                toggleVisiblefieldsGrid(file.id, event)
                              }
                              tabIndex="0"
                              onKeyUp={keyUpElement}
                            >
                              {file.fieldsGridVisible ? (
                                <i className="material-icons">expand_less</i>
                              ) : (
                                <i className="material-icons">expand_more</i>
                              )}

                              <span className="title">
                                {Realize.i18n.t(
                                  'importMultipleModal.fillLineFields'
                                )}
                              </span>
                            </div>

                            <div
                              className={`content-fields-grid ${
                                file.fieldsGridVisible ? 'is-open' : ''
                              }`}
                            >
                              <InputGroup
                                inputs={getFieldsGrid()}
                                errors={file.errorsGrid}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </Realize.ModalContent>
        <Realize.ModalFooter className={'right-align'}>
          {renderModalButtons()}
        </Realize.ModalFooter>
      </Realize.Modal>
    </div>
  );
};

export default ImportMultipleGridModal;
